<script lang="ts">
	import { create, type JsonObject } from "@bufbuild/protobuf";
	import { timestampNow, type Timestamp } from "@bufbuild/protobuf/wkt";
	import { getContext, onDestroy, onMount, setContext } from "svelte";
	import { readable, writable, type Readable, type Writable } from "svelte/store";
	import { twMerge } from "tailwind-merge";
	import type { BrandKit } from "#gen/brands/v1/service_pb.js";
	import { ConversionType, RenderMode, type AssetRef, type Suite } from "#gen/bundles/v1/bundle_pb.js";
	import { ConversionMetaDataSchema, InteractionSchema, InteractionType, type ConversionMetaData } from "#gen/bundles/v1/events_pb.js";
	import { Mode } from "#gen/bundles/v1/service_pb.js";
	import type { Page } from "#gen/pages/v2/page_pb.js";
	import type { AssetLoad } from "#lib/api/bundles.js";
	import type { EntryAsset } from "#lib/assetManager.js";
	import FooterLinks from "#lib/components/flow/viewer/FooterLinks.svelte";
	import { rendersAsFullPage, resolveBackgroundPaint } from "#lib/components/flow/viewer/utils.js";
	import DynamicComponent from "#lib/components/page/DynamicComponent.svelte";
	import PageBackgroundLayer, { hostBackgroundStyle } from "#lib/components/page/PageBackgroundLayer.svelte";
	import type { Registry } from "#lib/components/page/registry.js";
	import { mountBehaviors } from "#lib/components/page/utils.js";
	import type { BundleDag } from "#lib/dag.js";
	import { debounce } from "#lib/debounce.js";
	import { createInterpolatedDataStore, createInterpolatedPageStore } from "#lib/stores/asset.js";
	import { getBackgroundColor } from "#lib/theme.js";
	import type { TokenProvider } from "#lib/tokenProvider.js";
	import { browser } from "$app/env";

	export interface PageProps {
		asset: AssetLoad;
		mode: Mode;
		renderMode: RenderMode;
		parentMlRequestId?: string;
		readOnly?: boolean;
		previewInsetPadding?: boolean;
		modal?: boolean;
		// fullBleedContainer means the parent tray drawer already paints the page
		// background, so this page must not paint it again.
		fullBleedContainer?: boolean;
		interpolate?: boolean;
		themeStyle: string;
		class?: string;
		onExecute?: ((asset: AssetLoad, conversionType: ConversionType, conversionComponent: string, sourceAnchor: string | undefined, value: JsonObject, metadata: ConversionMetaData) => Promise<JsonObject | undefined>) | undefined;
		onReload?: (asset: AssetLoad, metadata: ConversionMetaData) => Promise<AssetLoad | undefined>;
		onCancel?: ((asset: AssetLoad, metadata: ConversionMetaData) => void) | undefined;
	}

	const tokenProvider: Writable<TokenProvider> = getContext("tokenProvider");
	const suite: Writable<Suite> = getContext("suite");
	const entryAsset: Writable<EntryAsset | undefined> = getContext("entryAsset");
	const registry: Registry = getContext("registry");
	const brandKit: Writable<BrandKit | undefined> = getContext("brandKit");
	const dag: Writable<BundleDag<AssetRef>> | undefined = getContext("dag");
	const isDesktop: Readable<boolean | undefined> = getContext<Readable<boolean | undefined> | undefined>("isDesktop") ?? readable(false);
	const scrollDebounce = debounce(recordScroll, 100);

	let { asset, mode = Mode.LIVE, renderMode = RenderMode.MODAL, parentMlRequestId, readOnly = false, previewInsetPadding = false, modal = false, fullBleedContainer = false, interpolate = false, onExecute, onReload, onCancel, themeStyle, class: className }: PageProps = $props();
	const pageState: Writable<Record<string, unknown>> = getContext("pageState");
	const rawPage = $derived(asset?.asPage() ?? writable<Page>());
	const interpolatedPage = $derived(createInterpolatedPageStore(tokenProvider, suite, entryAsset, rawPage, mode));
	const page = $derived(mode === Mode.EDITING && interpolate ? interpolatedPage : rawPage);
	const rawData = $derived(asset ? asset.data() : readable<JsonObject | undefined>());
	const interpolatedData = $derived(createInterpolatedDataStore(tokenProvider, suite, entryAsset, rawData, mode));
	const data = $derived(mode === Mode.EDITING && interpolate ? interpolatedData : rawData);
	const result = $derived(asset ? asset.result() : readable<JsonObject | undefined>());
	const backgroundColor = $derived(getBackgroundColor($brandKit, $page));
	const backgroundPaint = $derived(resolveBackgroundPaint({ mode, modal, renderMode, isDesktopUserAgent: $isDesktop, desktopViewEnabled: $suite?.desktopViewEnabled, fullBleedContainer }));
	const backgroundStyle = $derived(hostBackgroundStyle(backgroundPaint));
	const showFooterLinks = $derived(rendersAsFullPage($dag, asset?.key(), $suite.assets[asset?.key() ?? ""]));

	const components: Record<string, DynamicComponent> = $state({});
	const metadata = $state(create(ConversionMetaDataSchema, { interactions: [] }));
	let scrollStart: Timestamp | undefined;

	onMount(() => {
		metadata.pageloadTimestamp = timestampNow();
		const mlRequestId = asset.context()?.ml_request_id;
		if (mlRequestId) {
			metadata.mlRequestId = mlRequestId;
		}
		if (parentMlRequestId && mlRequestId !== parentMlRequestId) {
			metadata.parentMlRequestId = parentMlRequestId;
		}
	});

	$effect(() => {
		if (mode !== Mode.EDITING) {
			return mountBehaviors(rawPage, pageState);
		}
	});

	// The page paints the browser canvas behind it, which is what tints Safari's bars on iOS 26
	// where the theme-color meta is ignored.
	$effect(() => {
		if (backgroundPaint !== "layer") {
			return;
		}
		document.body.style.backgroundColor = backgroundColor;
	});

	onDestroy(() => {
		if (browser) {
			cancel();
		}
	});

	export function value(): JsonObject {
		const value: JsonObject = {};
		Object.keys(components).forEach(key => {
			components[key]?.value(value);
		});
		return value;
	}

	export async function validate(): Promise<boolean> {
		var valid = true;
		for (const key in components) {
			const component = components[key];
			if (component && component.validate) {
				valid = (await component.validate()) && valid;
			}
		}
		return valid;
	}

	async function execute(conversionType: ConversionType, conversionComponent: string, sourceAnchor?: string): Promise<JsonObject | boolean> {
		if (!sourceAnchor) {
			if (!(await validate())) {
				return false;
			}
		}
		const data = value();
		const result = await onExecute?.(asset, conversionType ?? ConversionType.UNSPECIFIED, conversionComponent, sourceAnchor, data, metadata);
		return result ?? true;
	}

	async function reload() {
		const result = await onReload?.(asset, metadata);
		if (result) {
			asset = result;
		}
	}

	function cancel() {
		onCancel?.(asset, metadata);
	}

	// Which component anchors have an outgoing link in the DAG, so connectable
	// components like Img can determine whether to act as a tappable link or
	// stay a static element.
	const connectedComponentIds = $derived.by(() => {
		const key = asset?.key();
		if (!key) {
			return new Set<string>();
		}
		const node = $dag?.getNode(key);
		if (!node) {
			return new Set<string>();
		}
		return new Set(node.linksOut.map(link => link.sourceAnchor).filter((anchor): anchor is string => Boolean(anchor)) ?? []);
	});

	function handleClick() {
		metadata.interactions.push(
			create(InteractionSchema, {
				interactionType: InteractionType.CLICK,
				timestamp: timestampNow(),
			}),
		);
	}

	function handleScroll() {
		if (!scrollStart) {
			scrollStart = timestampNow();
		}
		scrollDebounce();
	}

	function recordScroll() {
		metadata.interactions.push(
			create(InteractionSchema, {
				interactionType: InteractionType.SCROLL,
				timestamp: scrollStart ?? timestampNow(),
			}),
		);
		scrollStart = undefined;
	}

	setContext("isComponentConnected", (componentId: string) => connectedComponentIds.has(componentId));
	setContext("execute", execute);
	setContext("reload", reload);
</script>

<svelte:window on:beforeunload={cancel} />
<svelte:body on:scroll={handleScroll} on:click={handleClick} on:touchstart={handleClick} />

<div class={twMerge("flex h-full w-full min-w-0 grow flex-col", !modal && "justify-start @sm:items-center", backgroundPaint === "layer" && "relative isolate", className)} style={themeStyle + backgroundStyle}>
	{#if backgroundPaint === "layer"}
		<PageBackgroundLayer />
	{/if}
	<div class={twMerge("flex h-full w-full min-w-0 grow flex-col", !modal && "max-w-full @sm:mt-auto @sm:mb-auto @sm:w-110 @sm:overflow-clip", previewInsetPadding || !modal ? "p-4" : "")}>
		{#each [...Array(($page?.components?.length ?? 0) + 1)] as _, slotIdx (slotIdx)}
			{#if $page?.components && slotIdx < ($page?.components?.length ?? 0)}
				{@const component = $page?.components?.[slotIdx]}
				{@const isLast = slotIdx === ($page?.components?.length ?? 1) - 1}
				{#if component}
					<div data-page-component class="flex min-h-0 flex-col" class:flex-1={isLast}>
						<DynamicComponent bind:this={components[component?.id]} id={component.id} {component} definitions={registry.getDefinitions()} {page} {data} {result} {readOnly} {renderMode} {mode} />
					</div>
				{/if}
			{/if}
		{/each}
	</div>
	{#if showFooterLinks}
		<FooterLinks {backgroundColor} {mode} />
	{/if}
</div>
